import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProcosApi } from '../../api/ProcosAPi';
import {
  addMarketFeatureMA,
  createPaginatedFeatures,
  getFeatureStatus,
  getPMPPropertyStatus,
  removeMarketFeatureMA,
  revertFeaturesMA,
  mapMAToFeatureState,
  flipCountries,
} from '../../services/DataHelperFunctions';
import {
  Master,
  EErrorCode,
  EFeatureStatus,
  ERecordLength,
  ERuleSaveStatus,
  EWorkItemType,
} from '../../data/Constants';
import { isArraysEqual } from '../../services/Util';

import type {
  ICreateRuleRequest,
  IEffectivityRequest,
  IEffectivityResult,
  IFeature,
  IFeatureMaPayload,
  IFeaturesAssociations,
  IMarketAssociation,
  IProcosState,
  IWorkItemOption,
} from '../../../types';
import type {
  IMarketAssociationRequest,
  IReadFeatures,
  IReadMarketAssociations,
  IReadMarkets,
} from '../../../types/IRequestTypes';
import type { ISaveRuleResponse } from '../../../types/IApiTypes';

const initialState: IProcosState = {
  workItems: [],
  newWorkItem: null,
  activeWorkItem: null,
  isEditMode: false,
  isDataUpdated: false,
  isProductStatusUpdated: false,
  isDataReset: false,
  markets: [],
  product: {
    name: '',
    id: '',
    ma: {
      initialMarkets: [],
      updatedMarkets: [],
      type: '',
    }, //ma = Market Associations
  },
  features: {
    data: null,
    ma: {}, //ma = Market Associations,
    status: { updated: null, initial: null },
    recordsPerPage: ERecordLength.RL100,
  },
  saveData: null,
  productStatus: EFeatureStatus.Active,
  productStatusInMaster: EFeatureStatus.Active,
  featureStatus: false,
  effectivityResult: {},
  isCreateRuleInvoked: false,
  isBatchSaveComplete: false,
  batchSaveStatus: [],
};

/**
 * To handle Feature association checkbox selected
 * @param {IProcosState} state is Procos data
 * @param {action} action is redux action
 * @param {featureKey} featureKey is feature value
 * @returns {void}
 */
function handleCheckBoxSelected(
  state: IProcosState,
  action: PayloadAction<IFeatureMaPayload>,
  featureKey: IMarketAssociation
): void {
  state.features.ma[action.payload.id].updatedMarkets.push(action.payload.market);

  /*When initial countries length is 0 which represents it was added by user and the selections are
                     same as product maket selections, delete the association as there is no need to add rule to it */
  if (
    featureKey.initialMarkets.length === 0 &&
    state.product.ma?.updatedMarkets.length === featureKey.updatedMarkets.length
  ) {
    delete state.features.ma[action.payload.id];
  }
}

/**
 * To handle Feature association checkbox unselected
 * @param {IProcosState} state is Procos data
 * @param {action} action is redux action
 * @param {featureKey} featureKey is feature value
 * @returns {void}
 */
function handleCheckBoxUnselect(
  state: IProcosState,
  action: PayloadAction<IFeatureMaPayload>,
  featureKey: IMarketAssociation
) {
  const index = featureKey.updatedMarkets.indexOf(action.payload.market);

  /*When initial countries length is 0 which represents it was added by user and it is the last selection unchecked,
                    delete the association as there is no need to add rule to it */
  if (
    state.features.ma[action.payload.id].initialMarkets.length === 0 &&
    state.features.ma[action.payload.id].updatedMarkets.length === 1
  ) {
    delete state.features.ma[action.payload.id];
    if (state.features.status.updated) {
      state.features.status.updated[action.payload.id] = false;
    }
    return;
  }
  state.features.ma[action.payload.id].updatedMarkets.splice(index, 1);
  if (state.features.ma[action.payload.id].updatedMarkets.length === 0) {
    if (state.features.status.updated) {
      state.features.status.updated[action.payload.id] = false;
    }
  }
}

/**
 * reset status of feature save status
 * @param {IProcosState} state procos state
 * @returns {void}
 */
function resetFeatureSaveStatus(state: IProcosState) {
  const featuresData = state.features.data;
  featuresData?.map((feature: IFeature) => {
    if (feature.saveStatus) {
      feature.saveStatus = '';
    }
  });

  state.features.data = featuresData;
}

/**
 * To update Save status of feature effectivity once the API is processed
 * @param {IProcosState} state procos state
 * @param {[]} effectivityFeatures is all feature codes sent to Effectivity API calll
 * @param {IEffectivityResult} responseData is response from API
 * @returns {void}
 */
function updateSaveStatusOfEffectivity(
  state: IProcosState,
  effectivityFeatures: string[],
  responseData: IEffectivityResult = {}
) {
  const featuresData = state.features.data;
  // There are 3 cases to be covered here
  //Case 1: When all Effectivities are processed successfully - No codes are returned :: StatusCode: 200
  //Case 2: When Effectivities are partially processed successfully - Codes are returned as Dictionary :: StatusCode: 200
  //Case 3: When an exception is thrown by the API ie., no effectivities are processed ( failed ) :: StatusCode: other than 200
  const responseCodes: string[] = Object.keys(responseData) || []; //Case 1 & 2
  const isEffectivityFailedForAll = Object.keys(responseData || {}).includes(
    'AllEffectivityFailed'
  ); //Case 3

  featuresData?.map((feature: IFeature) => {
    for (const code of effectivityFeatures) {
      if (feature.code === code || code.includes(feature.code)) {
        const isEffectivityFailedForCode = responseCodes?.includes(code);
        feature.saveStatus =
          isEffectivityFailedForCode || isEffectivityFailedForAll
            ? ERuleSaveStatus.Failed
            : ERuleSaveStatus.Success;
      }
    }
  });
  state.features.data = featuresData;
}

/**
 * To update Save status of feature market rules
 * @param {IProcosState} state procos state
 * @param {ISaveRuleResponse} saveResponse is an array of failed rules
 * @param {[]} marketRuleFeatureCodes is an array of feature codes sent to API
 * @returns {void}
 */
function updateSaveStatusOfFeatureRules(
  state: IProcosState,
  saveResponse: ISaveRuleResponse,
  marketRuleFeatureCodes: string[]
): void {
  // Ensure we iterate over an array; `features.data` can be null
  const features: IFeature[] = state.features.data ?? [];

  // There are 3 cases to be covered here
  //Case 1: When all Market Rules are processed successfully - Successful feature codes returned :: StatusCode: 200
  //Case 2: When Market rules are partially processed successfully - Success and failed Codes are returned :: StatusCode: 200
  //Case 3: When an exception is thrown by the API ie., no Rules are processed ( failed ) :: StatusCode: other than 200

  // check whether the feature code available in the list
  const hasFeature = (code: string, list: string[]) =>
    list.includes(code) || list.some((item) => item.includes(code));

  for (const feature of features) {
    const code = feature.code;

    const hasfeatureCode = hasFeature(code, marketRuleFeatureCodes);

    if (hasfeatureCode) {
      const isRuleFailed = hasFeature(code, saveResponse.failed.rules);
      const isPropertyFailed = hasFeature(code, saveResponse.failed.properties);

      //updated the partial failed count
      feature.failed = {
        isPropertyFailed: isPropertyFailed,
        isRuleFailed: isRuleFailed,
      };

      if (isRuleFailed && isPropertyFailed) {
        feature.saveStatus = ERuleSaveStatus.Failed;
      } else if (isRuleFailed || isPropertyFailed) {
        feature.saveStatus = ERuleSaveStatus.Partial;
      } else {
        feature.saveStatus = ERuleSaveStatus.Success;
      }
    }
  }

  state.features.data = features;
}

/**
 * Update Save Status of batches of Create Rules API request
 * @param {IProcosState} state procos state
 * @param { ISaveRuleResponse } saveResponse is an array of failed rules
 * @returns {void}
 */
function updateSaveStatusOfBatches(state: IProcosState, saveResponse: ISaveRuleResponse) {
  const batchSaveData = state.batchSaveStatus;
  const updateBatch = batchSaveData.find((item) => item.batchStatus === 'P');

  if (!saveResponse?.isSuccess) {
    if (updateBatch) {
      updateBatch.batchStatus = 'F';
    }
  } else if (updateBatch) {
    updateBatch.batchStatus = 'S';
  }
}

/**
 * Checks if all market arrays in the feature data have the same length for initial and updated markets.
 *
 * @param {IFeaturesAssociations} featureData - The feature data containing each feature data.
 * @returns {boolean} - Returns `true` if all market arrays have equal lengths, otherwise `false`.
 */
function isAllFeatureMarketEqual(featureData: IFeaturesAssociations): boolean {
  return Object.values(featureData).every(({ initialMarkets, updatedMarkets }) =>
    isArraysEqual(initialMarkets, updatedMarkets)
  );
}

/**
 * Compares two objects representing active statuses and checks if they have the same keys and values.
 *
 * @param {Record<string, boolean>} initial - The initial active status object.
 * @param {Record<string, boolean>} updated - The updated active status object.
 * @returns {boolean} - Returns `true` if both objects have identical keys and values, otherwise `false`.
 */
function compareActiveStatus(
  initial: { [key: string]: boolean },
  updated: { [key: string]: boolean }
): boolean {
  const initialKeys = Object.keys(initial);
  const updatedKeys = Object.keys(updated);
  // Check if both objects have the same keys
  if (initialKeys.length !== updatedKeys.length) {
    return false;
  }

  // Check if values for each key match
  return initialKeys.every((key) => initial[key] === updated[key]);
}

//call the getWorkItems api and dispatch the response to the state
export const getWorkItems = createAsyncThunk('procos/getWorkItems', async () => {
  return await ProcosApi.getWorkItems();
});

//call the getMarkets api and dispatch the response to the state
export const getMarkets = createAsyncThunk('procos/getMarkets', async (action: IReadMarkets) => {
  return await ProcosApi.getMarkets(action);
});

//call the getMarketAssociations api and dispatch the response to the state
export const getMarketAssociations = createAsyncThunk(
  'procos/getMarketAssociation',
  async (action: IMarketAssociationRequest) => {
    const req: IReadMarketAssociations = {
      productCode: action.productCode,
      workItemId: action.workItemId,
      onlyMarketRules: action.onlyMarketRules,
    };
    return await ProcosApi.getMarketAssociations(req);
  }
);

export const getFeatures = createAsyncThunk('procos/getFeatures', async (action: IReadFeatures) => {
  return await ProcosApi.getFeatures(action);
});

//call create workitem api and dispatch the respose to the state
export const createWorkItem = createAsyncThunk('procos/createWorkItem', async (action: string) => {
  return await ProcosApi.createWorkItem(action);
});

//call create rule api and dispatch the respose to the state
export const createRules = createAsyncThunk(
  'procos/createRule',
  async (action: ICreateRuleRequest) => {
    return await ProcosApi.createRules(action);
  }
);

//call create effectivity api and dispatch the respose to the state
export const createEffectivity = createAsyncThunk(
  'procos/createEffectivity',
  async (action: IEffectivityRequest) => {
    return await ProcosApi.createEffectivity(action);
  }
);

//initialize the action to set the state variables
const ProcosSlice = createSlice({
  name: 'procos',
  initialState,
  reducers: {
    updateProductMarketAssociations(state, action) {
      state.isDataUpdated = true;
      state.isCreateRuleInvoked = false;
      resetFeatureSaveStatus(state);
      if (action.payload.checked) {
        //Checkbox is checked, add it to associations
        if (state.product.ma) {
          state.product.ma.updatedMarkets.push(action.payload.market);
        } else {
          state.product.ma = {
            initialMarkets: [],
            updatedMarkets: [action.payload.market],
          };
        }
        //Update Features Market associations to have the market added at Product level
        state.features.ma = addMarketFeatureMA(state.features.ma, action.payload.market);
      } else {
        //Unchecked checkbox hence remove it from associations
        const index = state.product.ma?.updatedMarkets.indexOf(action.payload.market);
        state.product.ma?.updatedMarkets.splice(index, 1);
        state.features.ma = removeMarketFeatureMA(
          state.features.ma,
          action.payload.market,
          state.product.ma?.updatedMarkets.length
        );
      }
      // if there are no changes to market at product level then disable the buttons
      if (isArraysEqual(state.product.ma.initialMarkets, state.product.ma.updatedMarkets)) {
        state.isDataUpdated = false;
      }
    },
    updateFeatureMarketAssociations(state, action) {
      const featureKey = state.features.ma?.[action.payload.id];
      if (!state.isDataUpdated) {
        state.isDataUpdated = true;
        state.isCreateRuleInvoked = false;
        resetFeatureSaveStatus(state);
      }
      if (featureKey) {
        //When feature is already present in associations, edit it
        if (action.payload.checked) {
          //checkbox is checked
          handleCheckBoxSelected(state, action, featureKey);
        } else {
          //checkbox is unchecked
          handleCheckBoxUnselect(state, action, featureKey);
        }
      } else {
        //When feature is not present in associations, Add it
        //since no key is present, its always uncheck case
        const newMarkets = state.product.ma?.updatedMarkets.filter(
          (market) => market !== action.payload.market
        );
        state.features.ma = {
          ...state.features.ma,
          [action.payload.id]: {
            initialMarkets: [],
            updatedMarkets: newMarkets,
          },
        };
        if (newMarkets && newMarkets.length === 0) {
          if (state.features.status.updated) {
            state.features.status.updated[action.payload.id] = false;
          }
        }
        //Add Feature Type
        state.features.ma[action.payload.id].type = action.payload.featureType;
      }
      const initialActiveStatus = state.features.status.initial;
      const updatedActiveStatus = state.features.status.updated;
      // if there are no changes to feature market associations and Active status
      // then disable the buttons
      if (
        isAllFeatureMarketEqual(state.features.ma) &&
        compareActiveStatus(initialActiveStatus || {}, updatedActiveStatus || {})
      ) {
        state.isDataUpdated = false;
      }
    },
    resetFeatureMA(state, action) {
      resetFeatureSaveStatus(state);
      if (!state.isDataUpdated) {
        state.isDataUpdated = true;
      }
      if (!action.payload.id) {
        state.features.status.updated = state.features.status
          ? state.features.status?.initial
          : null;
        //RESET ALL
        //loop through all associations and reset all.
        Object.keys(state.features.ma).forEach(function (key) {
          /* Initial countries 0 indicates they are selcted by user in current session and association 
                    can be deleted while reset since there is no need to create a rule as it already follows product level*/
          if (state.features.ma[key].initialMarkets.length === 0) {
            delete state.features.ma[key];
            return;
          }
          //Update the countries of features to Product Countries
          state.features.ma[key].updatedMarkets = [...state.product.ma.updatedMarkets];
        });
      } else {
        //RESET
        const featureKey = state.features.ma[action.payload.id];
        /* Initial countries 0 indicates they are selcted by user in current session and association 
                    can be deleted while reset since there is no need to create a rule as it already follows product level*/
        if (featureKey.initialMarkets.length === 0) {
          delete state.features.ma[action.payload.id];
          return;
        }
        //Update the countries of features to Product Countries
        state.features.ma[action.payload.id].type = action.payload.featureType;
        state.features.ma[action.payload.id].updatedMarkets = [...state.product.ma.updatedMarkets];
      }
    },
    setEditMode(state, action) {
      state.isEditMode = action.payload.isEdit;
    },
    setProduct(state, action) {
      state.product.name = action.payload.name;
      state.product.id = action.payload.id;
    },
    setActiveWorkItem(state, action) {
      state.activeWorkItem = action.payload;
    },
    revertMarketAssociations(state) {
      state.product.ma.updatedMarkets =
        state.product.ma !== null ? state.product.ma?.initialMarkets : [];
      state.features.status.updated = state.features.status ? state.features.status?.initial : null;
      state.features.ma = revertFeaturesMA(
        { ...state.features.ma },
        state.product.ma?.updatedMarkets
      );
      state.isDataUpdated = false;
      state.featureStatus = false;
      state.isProductStatusUpdated = false;
    },
    resetIsDataUpdated(state) {
      state.isDataUpdated = initialState.isDataUpdated;
    },
    setStatusOfAllRuleUpdates(state) {
      const featuresData = state.features.data;
      const featuresMA = state.features.ma;

      Object.keys(featuresMA).forEach((key) => {
        if (featuresMA[key].initialMarkets.length !== featuresMA[key].updatedMarkets.length) {
          featuresData?.map((feature: IFeature) => {
            // when key = FUS8528.NUSZ890 and feature.code = NUSZ890
            // This case is true when feature has option data
            if (feature.code === key || key.includes(feature.code)) {
              // For Effectivity feature code saveStatus will have either Success/Failure
              if (!feature.saveStatus) {
                feature.saveStatus = ERuleSaveStatus.Inprogress;
              }
            }
          });
        }
      });

      state.features.data = featuresData;
    },
    setBatchSaveStatus(state, action) {
      state.batchSaveStatus.push(action.payload);
    },
    resetBatchSaveStatus(state) {
      state.batchSaveStatus = [];
    },
    resetSaveStatusOfFeatures(state) {
      const featuresData = state.features.data;
      featuresData?.map((feature: IFeature) => {
        if (feature.saveStatus) {
          feature.saveStatus = '';
        }
      });

      state.features.data = featuresData;
    },
    resetProductData(state) {
      state.product = initialState.product;
      state.features = initialState.features;
      state.workItems = [];
    },
    setResetDataFlag(state, action) {
      state.isDataReset = action.payload;
    },
    setNewWorkItem(state, action) {
      state.newWorkItem = action.payload;
    },
    setProductStatus(state, action) {
      state.productStatus = action.payload;
      state.isDataUpdated = true;
      state.isProductStatusUpdated = true;
    },
    setFeatureStatus(state, action) {
      state.featureStatus = action.payload;
    },
    setFeatureData(state, action) {
      state.features.data = action.payload;
    },
    updateFeatureStatus(state, action) {
      state.features.status.updated = action.payload;
      resetFeatureSaveStatus(state);
      state.isDataUpdated = true;
    },
    updateToInitialFeatureMA(state, action) {
      if (action.payload) {
        action.payload.forEach((key: string) => {
          const featureKey = state.features.ma?.[key];
          if (featureKey) {
            state.features.ma = {
              ...state.features.ma,
              [key]: {
                initialMarkets: state.features.ma?.[key].initialMarkets,
                updatedMarkets: state.features.ma?.[key].updatedMarkets,
              },
            };
          } else {
            state.features.ma = {
              ...state.features.ma,
              [key]: {
                initialMarkets: [],
                updatedMarkets: state.product.ma?.updatedMarkets,
              },
            };
          }
        });
      }
    },
    setFeatureRecordsPerPage(state, action) {
      state.features.recordsPerPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWorkItems.fulfilled, (state, action) => {
        if ('error' in action.payload) {
          return;
        }
        let workItemList: IWorkItemOption[] = [Master];
        workItemList = [...workItemList, ...action.payload.data];
        state.workItems = workItemList;
      })
      .addCase(getMarkets.fulfilled, (state, action) => {
        if ('error' in action.payload) {
          state.markets = initialState.markets;
        } else {
          state.markets = action.payload.data;
        }
      })
      .addCase(getFeatures.fulfilled, (state, action) => {
        if ('error' in action.payload) {
          return;
        }
        state.productStatus = getPMPPropertyStatus(action.payload.data)
          ? EFeatureStatus.Active
          : EFeatureStatus.InActive;
        action.payload.data = action.payload.data.filter(
          (feature: IFeature) => feature.code !== 'PMP'
        );
        const featuresData = createPaginatedFeatures(action.payload, state.features.data);
        state.features.data = featuresData;
        state.features.status.initial = getFeatureStatus(action.payload);
        state.features.status.updated = state.features.status.initial;
        //Set the below state with product status in master
        //This is used in the places to perform functionalities based on Product's status in the Master (E.g.: Send To Sofon)
        if (state.activeWorkItem?.name === EWorkItemType.Master) {
          state.productStatusInMaster = state.productStatus;
        }
      })
      .addCase(getMarketAssociations.fulfilled, (state, action) => {
        state.isDataUpdated = false;
        state.isProductStatusUpdated = false;
        const allMarkets = state.markets.map((market) => market.code);
        if ('error' in action.payload) {
          state.features.ma = {};
          state.product.ma = initialState.product.ma;
        } else {
          const product = action.payload.data.find((x) => x.code === state.product.id);
          if (product) {
            product.countries =
              product.includeMarkets === false
                ? flipCountries(allMarkets, product.countries)
                : product.countries;
            state.product.ma = {
              initialMarkets: product.countries,
              updatedMarkets: product.countries,
            };
          }
          //state.product.rows = addRfq(product.countries, state.product.rows)
          state.features.ma = mapMAToFeatureState(
            action.payload.data,
            state.product.id,
            product.countries,
            allMarkets
          );
        }
      })
      .addCase(createWorkItem.fulfilled, (state, action) => {
        if ('error' in action.payload) {
          return;
        }
        const name = action.payload.data?.name;
        const id = action.payload.data?.id;
        if (!name || !id) {
          return;
        }
        state.activeWorkItem = { name, id };
      })
      .addCase(createRules.pending, (state) => {
        state.isCreateRuleInvoked = true;
        state.isBatchSaveComplete = false;
      })
      .addCase(createRules.fulfilled, (state, action) => {
        // Get all feature codes sent in payload to the API
        const marketRuleFeatureCodes = action?.meta?.arg?.request.marketRules?.map(
          (item) => item.code
        );

        state.isBatchSaveComplete =
          action?.meta?.arg?.isAllBatchesProcessed || !!action.payload.data?.errorId;

        // Get all feature codes which are failed to update
        if (action.payload.data) {
          //Dont reset the isDataUpdated flag  if API failed by network error to enable the Save and Revert button
          if (action.payload?.data?.errorId !== EErrorCode.NetworkError) {
            //Reset the isDataUpdated updated after all the batches are completed
            if (action?.meta?.arg?.isAllBatchesProcessed) {
              state.isDataUpdated = false;
            }

            //Reset the updated markets after api is completed
            //This should not update when api failed  by network issue
            marketRuleFeatureCodes.forEach((key) => {
              //update new rules to  initial markets for product
              if (state.product.id === key) {
                state.product.ma.initialMarkets = state.product.ma.updatedMarkets;
              }
              //update new rules to  initial markets for feature and options
              if (state.features.ma.hasOwnProperty(key)) {
                state.features.ma[key].initialMarkets = state.features.ma[key].updatedMarkets;
              }
            });
          }
          state.isProductStatusUpdated = false;
        }

        updateSaveStatusOfFeatureRules(state, action.payload.data, marketRuleFeatureCodes);
        updateSaveStatusOfBatches(state, action.payload.data);
      })
      .addCase(createEffectivity.fulfilled, (state, action) => {
        // Get all feature codes sent in payload to the API
        const effectivityFeatures = action?.meta?.arg?.features?.map((item) => item.code);
        // Get Response from Effectivity Save API
        const responseData = action.payload.data;

        if (responseData) {
          // To disable the Save and Revert buttons in ProCos make isDataUpdated false
          state.isDataUpdated = false;
          state.effectivityResult = responseData;
          const effectivityResult = responseData;
          if (Object.keys(effectivityResult).length !== 0) {
            if (state.product.id in effectivityResult) {
              state.productStatus =
                state.productStatus === EFeatureStatus.Active
                  ? EFeatureStatus.InActive
                  : EFeatureStatus.Active;
            }
            Object.keys(effectivityResult).forEach((key) => {
              if (state.features.status.initial && state.features.status.updated) {
                if (
                  key in effectivityResult &&
                  Object.keys(state.features.status.initial).includes(key)
                ) {
                  state.features.status.updated[key] = state.features.status.initial[key];
                }
              }
            });
          }
          // active status of features was not updated properly
          state.features.status.initial = state.features.status.updated;

          //Setting this state once effectivity API is executed
          updateSaveStatusOfEffectivity(state, effectivityFeatures, responseData);
        }
      });
  },
});

export const {
  updateToInitialFeatureMA,
  resetProductData,
  revertMarketAssociations,
  setProduct,
  updateProductMarketAssociations,
  updateFeatureMarketAssociations,
  setActiveWorkItem,
  resetIsDataUpdated,
  setResetDataFlag,
  setNewWorkItem,
  resetFeatureMA,
  setEditMode,
  setProductStatus,
  setFeatureStatus,
  setFeatureData,
  updateFeatureStatus,
  setBatchSaveStatus,
  resetBatchSaveStatus,
  resetSaveStatusOfFeatures,
  setFeatureRecordsPerPage,
  setStatusOfAllRuleUpdates,
} = ProcosSlice.actions;

export const procosSettings = (state: IProcosState) => state;

export default ProcosSlice.reducer;
